import { useEffect } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

const services = [
  {
    emoji: '⚡',
    name: 'Electrical Installations',
    slug: 'electrical-installations',
    description: 'Complete electrical installation services for new builds, extensions, and renovations throughout Watford and surrounding areas.'
  },
  {
    emoji: '🔌',
    name: 'Full & Partial Rewiring',
    slug: 'rewiring',
    description: 'Professional rewiring services to bring your property up to modern safety standards with minimal disruption to your home.'
  },
  {
    emoji: '🔧',
    name: 'Consumer Unit Upgrades',
    slug: 'consumer-unit-upgrades',
    description: 'Upgrade your outdated fuse box to a modern consumer unit with RCD protection for enhanced electrical safety.'
  },
  {
    emoji: '💡',
    name: 'Lighting Installation',
    slug: 'lighting-installation',
    description: 'Expert lighting installation for all rooms, from ambient ceiling lights to feature spotlights and downlights.'
  },
  {
    emoji: '🌿',
    name: 'LED Lighting Upgrades',
    slug: 'led-lighting',
    description: 'Energy-efficient LED lighting upgrades that reduce your electricity bills while providing superior illumination.'
  },
  {
    emoji: '🌳',
    name: 'Outdoor & Garden Lighting',
    slug: 'outdoor-garden-lighting',
    description: 'Transform your outdoor spaces with professional garden lighting, pathway lights, and feature illumination.'
  },
  {
    emoji: '🔦',
    name: 'Security Lighting',
    slug: 'security-lighting',
    description: 'Motion-activated security lighting and floodlights to protect your property and deter intruders.'
  },
  {
    emoji: '🔲',
    name: 'Socket & Switch Installation',
    slug: 'socket-switch',
    description: 'Additional sockets, USB outlets, and designer switches installed exactly where you need them in your home.'
  },
  {
    emoji: '🚗',
    name: 'EV Charger Installation',
    slug: 'ev-charger',
    description: 'OZEV-approved electric vehicle charging point installation for convenient home charging of your EV.'
  },
  {
    emoji: '🏠',
    name: 'Smart Home Installations',
    slug: 'smart-home',
    description: 'Smart lighting, heating, and security systems that you can control from your smartphone or voice assistant.'
  },
  {
    emoji: '🤖',
    name: 'Home Automation Systems',
    slug: 'home-automation',
    description: 'Integrated home automation solutions for lighting, heating, security, and entertainment systems.'
  },
  {
    emoji: '🌡️',
    name: 'Heating Controls & Thermostats',
    slug: 'heating-controls',
    description: 'Smart thermostat installation and heating control upgrades for improved comfort and energy efficiency.'
  },
  {
    emoji: '🚿',
    name: 'Electric Shower Installation',
    slug: 'electric-shower',
    description: 'Professional electric shower installation with dedicated circuits and proper water pressure optimization.'
  },
  {
    emoji: '🍳',
    name: 'Kitchen Electrical Installations',
    slug: 'kitchen-electrical',
    description: 'Complete kitchen electrical services including cooker circuits, appliance connections, and under-cabinet lighting.'
  }
];

export default function InstallationsPage() {
  useEffect(() => {
    document.title = 'Electrical Installations Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional electrical installations in Watford & Hertfordshire. NICEIC approved for rewiring, LED lighting, EV chargers, smart home & consumer unit upgrades. 15+ years experience. Call +44 7989 675952.');
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      (metaKeywords as HTMLMetaElement).name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    (metaKeywords as HTMLMetaElement).content = 'electrical installations Watford, rewiring Watford, EV charger Watford, consumer unit upgrade Watford, NICEIC electrician Watford';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'installations-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": "08:00",
            "closes": "18:00"
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Electrical Installation Services Watford",
        "description": "Professional electrical installation and upgrade services for homes and businesses across Watford, Rickmansworth, and surrounding areas.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "Electrical Installations",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations`
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('installations-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/1af9df8d7307d2e03c4460bffa4226ae.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Approved
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Serving Watford & Beyond
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-time-fill"></i>
              15 Years Experience
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Installation & Upgrades<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional electrical installation and upgrade services for homes and businesses across Watford, Rickmansworth, and surrounding areas. From complete rewiring to smart home systems, we deliver quality workmanship backed by NICEIC certification.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+447989675952"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              +44 7989 675952
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Installation Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive electrical installation and upgrade solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <a
                key={service.slug}
                href={`/services/installations/${service.slug}`}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#FFD700] cursor-pointer"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-xl text-3xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    {service.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2 group-hover:text-[#FFD700] transition-colors duration-300">
                      {service.name}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <div className="flex items-center text-[#FFD700] font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Learn More
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted local electricians in Watford with a proven track record
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                🏆
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">15 Years Experience</h3>
              <p className="text-gray-300 leading-relaxed">
                Over a decade of delivering exceptional electrical services across Watford and surrounding areas
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">NICEIC Certified</h3>
              <p className="text-gray-300 leading-relaxed">
                Fully qualified and certified electricians ensuring all work meets the highest safety standards
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                📍
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Local to Watford</h3>
              <p className="text-gray-300 leading-relaxed">
                Based in Watford, we provide fast response times and personalized service to our local community
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">500+</div>
              <div className="text-gray-300 text-sm">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">100%</div>
              <div className="text-gray-300 text-sm">Customer Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">24/7</div>
              <div className="text-gray-300 text-sm">Emergency Service</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">15+</div>
              <div className="text-gray-300 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Areas We Cover
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Providing professional electrical installation services throughout Watford, Rickmansworth, and surrounding areas including:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Upgrade Your Electrical System?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Get in touch today for a free, no-obligation quote. Our expert team is ready to help with all your electrical installation needs in Watford and beyond.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+447989675952"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call +44 7989 675952
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Request a Quote
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#FFD700] text-sm font-semibold">
            <i className="ri-flashlight-fill"></i>
            24/7 Emergency Callout Available
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}